import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { usePlaidLink } from 'react-plaid-link';
import axios from 'axios';
import { ShieldCheckIcon, LinkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import WizardStepIndicator from './common/WizardStepIndicator';
import CashflowEntryForm from './cashflow/CashflowEntryForm';

const TOTAL_STEPS = 4;
const fade = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: 'easeOut' },
};

const OnboardingFlow = ({ user, onComplete }) => {
  const [step, setStep] = useState(1);
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shouldOpen, setShouldOpen] = useState(false);
  const currencyDetectedRef = useRef(false);

  useEffect(() => {
    if (!user?.user_id || currencyDetectedRef.current) return;
    currencyDetectedRef.current = true;
    const locale =
      (typeof navigator !== 'undefined' && navigator.languages?.[0]) ||
      navigator.language ||
      'en-US';
    axios
      .post('/api/user/currency/detect', { locale })
      .catch(() => {});
  }, [user?.user_id]);

  const getLinkToken = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/plaid/create_link_token');
      setLinkToken(response.data.link_token);
    } catch {
      setError('Failed to initialize secure connection. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onSuccess = useCallback(
    async (publicToken) => {
      if (!user?.user_id) return;

      setLoading(true);
      try {
        await axios.post('/api/plaid/exchange_public_token', {
          public_token: publicToken,
          user_id: user.user_id,
        });
        setStep(4);
        setTimeout(() => {
          onComplete?.({ ...user, portfolio_connected: true, onboarding_complete: true });
        }, 2000);
      } catch {
        setError('Failed to connect your portfolio. Please try again.');
        setLoading(false);
      }
    },
    [user, onComplete]
  );

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit: () => setLoading(false),
    onEvent: () => {},
  });

  useEffect(() => {
    if (ready && shouldOpen) {
      open();
      setShouldOpen(false);
    }
  }, [ready, shouldOpen, open]);

  const handleSkip = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post('/api/onboarding/skip');
      onComplete?.({ ...user, onboarding_complete: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not skip. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCashflowSaved = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post('/api/onboarding/skip');
      onComplete?.({ ...user, onboarding_complete: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not finish setup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectClick = async () => {
    if (!linkToken) {
      await getLinkToken();
      setShouldOpen(true);
    } else if (ready) {
      open();
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <p className="text-body text-secondary">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-4 py-12">
      <motion.div
        className="w-full max-w-lg"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <WizardStepIndicator currentStep={step} totalSteps={TOTAL_STEPS} />

        {error && (
          <div
            className="mb-6 p-4 rounded-md border border-danger bg-bone-raised text-body text-danger"
            role="alert"
          >
            {error}
          </div>
        )}

        {step === 1 && (
          <motion.div key="welcome" {...fade} className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full border border-hairline bg-bone-raised flex items-center justify-center">
              <ShieldCheckIcon className="w-8 h-8 text-pine" />
            </div>
            <h1 className="font-display text-h1 text-primary mb-4">
              Welcome to Aura
            </h1>
            <p className="text-body text-secondary mb-6 leading-relaxed max-w-md mx-auto">
              Aura is manual-first: start with your income and expenses so we can show a clear
              cashflow picture. You can also connect a portfolio with Plaid, or do that later in
              Account Settings.
            </p>
            <ul className="text-left text-body text-secondary space-y-3 mb-8 max-w-sm mx-auto">
              <li className="flex gap-3">
                <CheckCircleIcon className="w-5 h-5 text-pine shrink-0" />
                Bank-level encryption when you connect accounts
              </li>
              <li className="flex gap-3">
                <CheckCircleIcon className="w-5 h-5 text-pine shrink-0" />
                Read-only access — we never store credentials or make trades
              </li>
              <li className="flex gap-3">
                <CheckCircleIcon className="w-5 h-5 text-pine shrink-0" />
                You can update or connect accounts anytime
              </li>
            </ul>
            <button
              type="button"
              onClick={() => setStep(2)}
              className="btn-primary w-full py-3 mb-3"
            >
              Continue
            </button>
            <button
              type="button"
              onClick={() => setStep(3)}
              className="btn-secondary w-full py-3 mb-3"
            >
              Connect portfolio with Plaid
            </button>
            <button
              type="button"
              onClick={handleSkip}
              disabled={loading}
              className="btn-ghost w-full py-3 disabled:opacity-50"
            >
              {loading ? 'Loading…' : 'Skip for now — set up later'}
            </button>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="cashflow" {...fade}>
            <h1 className="font-display text-h1 text-primary mb-2 text-center">
              Your income and expenses
            </h1>
            <p className="text-body text-secondary mb-6 text-center max-w-md mx-auto">
              A quick snapshot is enough. Charts and Aura unlock after you save.
            </p>
            <CashflowEntryForm embedded onSaved={handleCashflowSaved} />
            <div className="flex flex-col gap-2 mt-6 pt-6 border-t border-hairline">
              <button type="button" onClick={() => setStep(3)} className="btn-secondary w-full py-3">
                Connect portfolio with Plaid instead
              </button>
              <button
                type="button"
                onClick={handleSkip}
                disabled={loading}
                className="btn-ghost w-full py-3 disabled:opacity-50"
              >
                Skip for now
              </button>
              <button type="button" onClick={() => setStep(1)} className="btn-ghost w-full text-label">
                Back
              </button>
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="plaid" {...fade} className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full border border-hairline bg-bone-raised flex items-center justify-center">
              <LinkIcon className="w-8 h-8 text-pine" />
            </div>
            <h1 className="font-display text-h1 text-primary mb-4">Connect your portfolio</h1>
            <p className="text-body text-secondary mb-8 leading-relaxed">
              Open the secure Plaid window to link your brokerage. You can skip and add accounts
              manually later.
            </p>
            <button
              type="button"
              onClick={handleConnectClick}
              disabled={loading}
              className="btn-primary w-full py-3 disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-bone border-t-transparent rounded-full animate-spin" />
                  Connecting…
                </span>
              ) : (
                'Open secure connection'
              )}
            </button>
            <button type="button" onClick={() => setStep(2)} className="btn-ghost w-full mt-4 py-3">
              Back to cashflow setup
            </button>
            <button type="button" onClick={() => setStep(1)} className="btn-ghost w-full mt-2 text-label">
              Back to welcome
            </button>
          </motion.div>
        )}

        {step === 4 && (
          <motion.div key="success" {...fade} className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full border border-hairline bg-pine-tint flex items-center justify-center">
              <CheckCircleIcon className="w-10 h-10 text-pine" />
            </div>
            <h1 className="font-display text-h1 text-primary mb-4">Portfolio connected</h1>
            <p className="text-body text-secondary mb-8">
              Your financial journey with Aura begins now. Preparing your dashboard…
            </p>
            <div className="flex justify-center gap-2" aria-hidden>
              {[0, 1, 2].map((i) => (
                <span key={i} className="w-2 h-2 rounded-full bg-pine opacity-50" />
              ))}
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default OnboardingFlow;
